import { useMemo } from 'react';
import {
  Box,
  Heading,
  InlineStack,
  Panel,
  PanelSection,
  Paragraph,
  Radio,
  Text,
  Toggle,
  Tooltip,
  TooltipContent,
} from '@customerio/pluma-components/react';
import { GroupedChecklist, type ToggleDelta } from '@/components/grouped-checklist';
import { isMParticleEnableLocked } from '@/services/db/mparticle-integration/entity';
import { useProductTaxonomy } from '@/services/product-taxonomy';
import { getMParticleEventGroups } from '@/utils/mparticle-events';

export type MParticleSettings = {
  enabled: boolean;
  events: string[];
  resolution: boolean;
};

type SettingsFormProps = {
  settings: MParticleSettings;
  isReadOnly: boolean;
  onChange: (settings: MParticleSettings) => void;
};

/** Which events to forward, whether forwarding is on, and how often to send. */
export function SettingsForm({ settings, isReadOnly, onChange }: SettingsFormProps) {
  const productTaxonomy = useProductTaxonomy();
  const groups = useMemo(() => [...getMParticleEventGroups({ productTaxonomy })], [productTaxonomy]);

  const isEnableLocked = isMParticleEnableLocked(settings);

  return (
    <>
      <PanelSection>
        <InlineStack alignBlock="center" alignInline="space-between">
          <Box flex={1} maxW="full" minW="0">
            <Heading level="2" text="product-h4" mb="100">
              Integration state
            </Heading>
            <Paragraph mb="200" color="subtle" size="xs">
              Note: If you disable, your integration settings will be saved, but no events will be sent.
            </Paragraph>
          </Box>

          {/*
            The tooltip explains why the toggle is dead, so it only exists while
            it is — and it anchors to the Toggle itself, whose field wrapper still
            fires pointer events while the input is disabled.
          */}
          <Tooltip isCustom isDisabled={!isEnableLocked}>
            {(context) => (
              <>
                <Toggle
                  ref={context.reference.setReference}
                  {...context.reference.getReferenceProps()}
                  ariaLabel="Integration state"
                  isChecked={settings.enabled}
                  isDisabled={isReadOnly || isEnableLocked}
                  onChange={(event) => onChange({ ...settings, enabled: event.currentTarget.checked })}
                  size="sm"
                />
                <TooltipContent>To enable, you must select at least one event to send.</TooltipContent>
              </>
            )}
          </Tooltip>
        </InlineStack>
      </PanelSection>

      <PanelSection>
        <Box as="fieldset" mb="200">
          <Text text="product-h4" as="legend" mb="150">
            Select events
          </Text>
          <GroupedChecklist
            isDisabled={isReadOnly}
            groups={groups}
            selectedItems={settings.events}
            onChange={(delta: ToggleDelta) => onChange({ ...settings, events: delta.selectedItems })}
          />
        </Box>

        <Panel backgroundColor="subtle">
          <PanelSection>
            <Box as="fieldset">
              <Text text="product-h4" as="legend" mb="150">
                Choose send frequency for opens and clicks
              </Text>

              <Radio
                id="resolution_first_time"
                name="resolution"
                label="Send only the first time the event occurs"
                isChecked={!settings.resolution}
                isDisabled={isReadOnly}
                onCheckedChange={() => onChange({ ...settings, resolution: false })}
              />
              <Radio
                id="resolution_every_time"
                name="resolution"
                label="Send every time the event occurs"
                isChecked={settings.resolution}
                isDisabled={isReadOnly}
                onCheckedChange={() => onChange({ ...settings, resolution: true })}
              />
            </Box>
          </PanelSection>
        </Panel>
      </PanelSection>
    </>
  );
}
